import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../config';

interface BankAccount {
  id: number;
  account_number?: string;
  bank_code?: string;
  bank_name?: string;
  iban?: string;
  is_default?: boolean;
}

interface Supplier {
  id: number;
  name: string;
  ico?: string;
  dic?: string;
  vat_payer?: boolean;
  address?: string;
  phone?: string;
  email?: string;
  bank_accounts?: BankAccount[];
}

interface Client {
  id: number;
  name: string;
  ico?: string;
  dic?: string;
  invoice_email?: string;
  due_days?: number;
  variable_symbol?: string;
  address?: string;
}

interface InvoiceItem {
  name: string;
  quantity: number;
  price_per_unit: string;
}

export interface InvoicePrefill {
  suggestedItem?: InvoiceItem;
  dateCreated?: string;
  dateDue?: string;
  defaults?: {
    client_id?: number | null;
    supplier_id?: number | null;
    bank_account_id?: number | null;
  };
  totalHours?: number;
  totalAmount?: number;
  hourlyRate?: number;
  periodLabel?: string;
}

interface InvoiceModalProps {
  onClose: () => void;
  prefill?: InvoicePrefill | null;
  onSuccess?: (invoice: { id: number; invoice_number?: string }) => void;
}

const formatMoney = (n: number) =>
  n.toLocaleString('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' Kč';

const parsePrice = (v: string) => {
  const n = Number(String(v).replace(',', '.').replace(/\s/g, ''));
  return Number.isFinite(n) ? n : 0;
};

const InvoiceModal: React.FC<InvoiceModalProps> = ({ onClose, prefill, onSuccess }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{ id: number; invoice_number?: string } | null>(null);

  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [supplierId, setSupplierId] = useState<number | ''>('');
  const [clientId, setClientId] = useState<number | ''>('');
  const [bankAccountId, setBankAccountId] = useState<number | ''>('');
  const [dateCreated, setDateCreated] = useState('');
  const [dateDue, setDateDue] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [items, setItems] = useState<InvoiceItem[]>([
    { name: '', quantity: 1, price_per_unit: '0.00' },
  ]);
  const [configured, setConfigured] = useState(true);
  const [apiKeyInput, setApiKeyInput] = useState('');

  const selectedSupplier = suppliers.find((s) => s.id === supplierId);
  const selectedClient = clients.find((c) => c.id === clientId);
  const bankAccounts = selectedSupplier?.bank_accounts || [];

  const total = useMemo(
    () => items.reduce((sum, it) => sum + it.quantity * parsePrice(it.price_per_unit), 0),
    [items]
  );

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const settingsRes = await axios.get(`${API_URL}/api/invoice/settings`);
        setConfigured(!!settingsRes.data?.configured);

        if (!settingsRes.data?.configured) {
          setLoading(false);
          return;
        }

        const [supRes, cliRes] = await Promise.all([
          axios.get(`${API_URL}/api/invoice/suppliers`),
          axios.get(`${API_URL}/api/invoice/clients`),
        ]);
        const supList: Supplier[] = supRes.data?.data || [];
        const cliList: Client[] = cliRes.data?.data || [];
        setSuppliers(supList);
        setClients(cliList);

        const defSup =
          prefill?.defaults?.supplier_id ||
          settingsRes.data?.default_supplier_id ||
          supList[0]?.id;
        const defCli =
          prefill?.defaults?.client_id ||
          settingsRes.data?.default_client_id ||
          cliList[0]?.id;

        if (defSup) setSupplierId(defSup);
        if (defCli) setClientId(defCli);

        if (prefill?.dateCreated) {
          setDateCreated(prefill.dateCreated);
        } else {
          // Fallback: 1st of current month
          const now = new Date();
          setDateCreated(
            `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`
          );
        }
        if (prefill?.dateDue) {
          setDateDue(prefill.dateDue);
        } else if (prefill?.dateCreated) {
          // 22nd of the same month as created
          const [y, m] = prefill.dateCreated.split('-');
          setDateDue(`${y}-${m}-22`);
        } else {
          const now = new Date();
          setDateDue(
            `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-22`
          );
        }

        if (prefill?.suggestedItem) {
          setItems([{ ...prefill.suggestedItem }]);
        }
      } catch (err: any) {
        setError(err.response?.data?.detail || err.message || 'Nepodařilo se načíst data fakturace');
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [prefill]);

  useEffect(() => {
    if (!selectedSupplier) {
      setBankAccountId('');
      return;
    }
    const accounts = selectedSupplier.bank_accounts || [];
    const preferred =
      prefill?.defaults?.bank_account_id ||
      accounts.find((a) => a.is_default)?.id ||
      accounts[0]?.id;
    setBankAccountId(preferred || '');
  }, [selectedSupplier, prefill?.defaults?.bank_account_id]);

  useEffect(() => {
    // When dates come from timesheet (1st / 22nd), don't override with client due_days
    if (prefill?.dateCreated || prefill?.dateDue) return;
    if (!selectedClient || !dateCreated) return;
    const [y, m] = dateCreated.split('-');
    if (y && m) setDateDue(`${y}-${m}-22`);
  }, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveApiKey = async () => {
    if (!apiKeyInput.trim()) return;
    try {
      await axios.put(`${API_URL}/api/invoice/settings`, {
        api_key: apiKeyInput.trim(),
      });
      setConfigured(true);
      setApiKeyInput('');
      // reload
      window.location.reload();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Uložení klíče selhalo');
    }
  };

  const updateItem = (index: number, patch: Partial<InvoiceItem>) => {
    setItems((prev) => prev.map((it, i) => (i === index ? { ...it, ...patch } : it)));
  };

  const addItem = () => {
    setItems((prev) => [...prev, { name: '', quantity: 1, price_per_unit: '0.00' }]);
  };

  const removeItem = (index: number) => {
    setItems((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
  };

  const handleCreate = async () => {
    if (!supplierId || !clientId) {
      setError('Vyber dodavatele a klienta');
      return;
    }
    if (!items.length || items.some((it) => !it.name.trim())) {
      setError('Každá položka musí mít název');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload: any = {
        supplier_id: supplierId,
        client_id: clientId,
        items: items.map((it) => ({
          name: it.name.trim(),
          quantity: Math.max(1, Math.round(Number(it.quantity) || 1)),
          price_per_unit: parsePrice(it.price_per_unit).toFixed(2),
        })),
        date_created: dateCreated || undefined,
        date_due: dateDue || undefined,
      };
      if (bankAccountId) payload.bank_account_id = bankAccountId;
      if (invoiceNumber.trim()) payload.invoice_number = invoiceNumber.trim();

      const res = await axios.post(`${API_URL}/api/invoice/invoices`, payload);
      const invoice = res.data?.invoice;
      setSuccess({ id: invoice.id, invoice_number: invoice.invoice_number });
      onSuccess?.(invoice);

      // Remember last supplier/client
      await axios.put(`${API_URL}/api/invoice/settings`, {
        default_supplier_id: supplierId,
        default_client_id: clientId,
        default_bank_account_id: bankAccountId || undefined,
      }).catch(() => undefined);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Vytvoření faktury selhalo');
    } finally {
      setSaving(false);
    }
  };

  const downloadPdf = async () => {
    if (!success?.id) return;
    const res = await axios.get(`${API_URL}/api/invoice/invoices/${success.id}/pdf`, {
      responseType: 'blob',
    });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = `faktura-${success.invoice_number || success.id}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const accountLabel = (a: BankAccount) => {
    const num = [a.account_number, a.bank_code].filter(Boolean).join('/');
    return `${num || a.iban || a.id}${a.bank_name ? ` · ${a.bank_name}` : ''}${a.is_default ? ' (výchozí)' : ''}`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="bg-white dark:bg-slate-900 rounded-2xl shadow-panel border border-slate-200 dark:border-slate-700 w-full max-w-4xl max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-700 flex items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-extrabold text-slate-900 dark:text-white">
              {success ? `Faktura č. ${success.invoice_number || success.id}` : 'Vygenerovat fakturu'}
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {prefill?.periodLabel
                ? `Z worklogu · ${prefill.periodLabel}`
                : 'Předvyplněno z Jiry / fakturačního systému'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 dark:hover:text-white text-xl leading-none px-1"
          >
            ×
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {loading ? (
            <div className="text-sm text-slate-400 py-10 text-center">Načítám dodavatele a klienty…</div>
          ) : !configured ? (
            <div className="space-y-3 max-w-lg mx-auto py-6">
              <p className="text-sm text-slate-600 dark:text-slate-300">
                Zadej API klíč fakturačního systému (<code className="text-xs">X-Api-Key</code>).
              </p>
              <input
                type="password"
                value={apiKeyInput}
                onChange={(e) => setApiKeyInput(e.target.value)}
                placeholder="API klíč"
                className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-xl bg-transparent dark:text-white"
              />
              <button
                type="button"
                onClick={saveApiKey}
                className="px-4 py-2 text-sm font-semibold rounded-xl bg-complic-600 text-white"
              >
                Uložit a pokračovat
              </button>
            </div>
          ) : success ? (
            <div className="text-center py-10 space-y-4">
              <div className="text-4xl">✅</div>
              <p className="text-sm text-slate-700 dark:text-slate-200">
                Faktura <strong>{success.invoice_number || success.id}</strong> byla vytvořena.
              </p>
              <div className="flex justify-center gap-2">
                <button
                  type="button"
                  onClick={downloadPdf}
                  className="px-4 py-2 text-sm font-semibold rounded-xl bg-complic-600 text-white"
                >
                  Stáhnout PDF
                </button>
                <a
                  href="https://invoice.nezval.opik.net"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 text-sm rounded-xl border border-slate-200 dark:border-slate-600"
                >
                  Otevřít fakturaci
                </a>
              </div>
            </div>
          ) : (
            <>
              {error && (
                <div className="text-sm text-red-600 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded-xl px-3 py-2">
                  {error}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-3 space-y-2">
                  <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Dodavatel</div>
                  <select
                    value={supplierId}
                    onChange={(e) => setSupplierId(Number(e.target.value))}
                    className="w-full px-2 py-1.5 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-transparent dark:text-white"
                  >
                    <option value="">— vyber —</option>
                    {suppliers.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                  {selectedSupplier && (
                    <div className="text-xs text-slate-500 space-y-0.5">
                      {selectedSupplier.ico && <div>IČO: {selectedSupplier.ico}</div>}
                      {selectedSupplier.dic && <div>DIČ: {selectedSupplier.dic}</div>}
                    </div>
                  )}
                </div>

                <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-3 space-y-2">
                  <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Odběratel</div>
                  <select
                    value={clientId}
                    onChange={(e) => setClientId(Number(e.target.value))}
                    className="w-full px-2 py-1.5 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-transparent dark:text-white"
                  >
                    <option value="">— vyber —</option>
                    {clients.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                  {selectedClient && (
                    <div className="text-xs text-slate-500 space-y-0.5">
                      {selectedClient.ico && <div>IČO: {selectedClient.ico}</div>}
                      {selectedClient.dic && <div>DIČ: {selectedClient.dic}</div>}
                      {selectedClient.invoice_email && <div>{selectedClient.invoice_email}</div>}
                    </div>
                  )}
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <label className="text-xs space-y-1 block">
                  <span className="font-semibold text-slate-500">Bankovní účet</span>
                  <select
                    value={bankAccountId}
                    onChange={(e) => setBankAccountId(Number(e.target.value))}
                    className="w-full px-2 py-1.5 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-transparent dark:text-white"
                  >
                    <option value="">výchozí</option>
                    {bankAccounts.map((a) => (
                      <option key={a.id} value={a.id}>
                        {accountLabel(a)}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-xs space-y-1 block">
                  <span className="font-semibold text-slate-500">Datum vytvoření</span>
                  <input
                    type="date"
                    value={dateCreated}
                    onChange={(e) => setDateCreated(e.target.value)}
                    className="w-full px-2 py-1.5 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-transparent dark:text-white"
                  />
                </label>
                <label className="text-xs space-y-1 block">
                  <span className="font-semibold text-slate-500">Datum splatnosti</span>
                  <input
                    type="date"
                    value={dateDue}
                    onChange={(e) => setDateDue(e.target.value)}
                    className="w-full px-2 py-1.5 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-transparent dark:text-white"
                  />
                </label>
                <label className="text-xs space-y-1 block">
                  <span className="font-semibold text-slate-500">Číslo faktury</span>
                  <input
                    type="text"
                    value={invoiceNumber}
                    onChange={(e) => setInvoiceNumber(e.target.value)}
                    placeholder="auto"
                    className="w-full px-2 py-1.5 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-transparent dark:text-white"
                  />
                </label>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-bold text-slate-800 dark:text-slate-100">Položky faktury</h4>
                  <button
                    type="button"
                    onClick={addItem}
                    className="text-xs font-semibold text-complic-600 hover:underline"
                  >
                    + Přidat položku
                  </button>
                </div>
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-200 dark:border-slate-700">
                      <th className="py-2 pr-2">Název položky</th>
                      <th className="py-2 pr-2 w-20">Počet</th>
                      <th className="py-2 pr-2 w-32">Cena za kus</th>
                      <th className="py-2 pr-2 w-28 text-right">Celkem</th>
                      <th className="py-2 w-10" />
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it, idx) => (
                      <tr key={idx} className="border-b border-slate-100 dark:border-slate-800">
                        <td className="py-1.5 pr-2">
                          <input
                            type="text"
                            value={it.name}
                            onChange={(e) => updateItem(idx, { name: e.target.value })}
                            className="w-full px-2 py-1 border border-slate-200 dark:border-slate-600 rounded-lg bg-transparent dark:text-white"
                          />
                        </td>
                        <td className="py-1.5 pr-2">
                          <input
                            type="number"
                            min={1}
                            step={1}
                            value={it.quantity}
                            onChange={(e) =>
                              updateItem(idx, { quantity: Math.max(1, Number(e.target.value) || 1) })
                            }
                            className="w-full px-2 py-1 border border-slate-200 dark:border-slate-600 rounded-lg bg-transparent dark:text-white"
                          />
                        </td>
                        <td className="py-1.5 pr-2">
                          <input
                            type="text"
                            value={it.price_per_unit}
                            onChange={(e) => updateItem(idx, { price_per_unit: e.target.value })}
                            className="w-full px-2 py-1 border border-slate-200 dark:border-slate-600 rounded-lg bg-transparent dark:text-white tabular-nums"
                          />
                        </td>
                        <td className="py-1.5 pr-2 text-right tabular-nums font-semibold">
                          {formatMoney(it.quantity * parsePrice(it.price_per_unit))}
                        </td>
                        <td className="py-1.5 text-center">
                          <button
                            type="button"
                            onClick={() => removeItem(idx)}
                            className="text-red-500 text-xs hover:underline"
                            disabled={items.length <= 1}
                          >
                            ×
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={3} className="pt-3 text-right font-bold text-slate-700 dark:text-slate-200">
                        Cena celkem
                      </td>
                      <td className="pt-3 text-right font-extrabold text-complic-700 dark:text-complic-300 tabular-nums">
                        {formatMoney(total)}
                      </td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
                {prefill?.totalHours != null && (
                  <p className="text-[11px] text-slate-400 mt-2">
                    Z worklogu: {prefill.totalHours}h × {prefill.hourlyRate?.toLocaleString('cs-CZ')} Kč/h
                  </p>
                )}
              </div>
            </>
          )}
        </div>

        {!success && configured && !loading && (
          <div className="px-5 py-4 border-t border-slate-100 dark:border-slate-700 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm rounded-xl border border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-800"
            >
              Zrušit
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={handleCreate}
              className="px-4 py-2 text-sm font-semibold rounded-xl bg-complic-600 text-white hover:bg-complic-700 disabled:opacity-50"
            >
              {saving ? 'Vytvářím…' : 'Vytvořit fakturu'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default InvoiceModal;
